/**
 * Utility functions for handling domain operations
 */

/**
 * Extracts the subdomain part from a full URL
 * @param {string|object} url - The URL to extract the subdomain from
 * @returns {string} - The extracted subdomain or empty string if invalid
 */
export const extractSubdomain = (urlInput) => {
  if (!urlInput) return '';

  let url = '';
  if (typeof urlInput === 'object' && urlInput !== null && typeof urlInput.url === 'string') {
    url = urlInput.url;
  } else if (typeof urlInput === 'string') {
    url = urlInput;
  } else {
    return ''; // Input non valido
  }

  try {
    // Rimuovi http(s):// se presente, per normalizzare l'input
    const processedUrl = url.replace(/^https?:\/\//, '');

    // Controlla se è un dominio .webvto.it e estrai il sottodominio
    if (processedUrl.includes('.webvto.it')) {
      const match = processedUrl.match(/^([^.]+)\.webvto\.it/); // Deve iniziare con il sottodominio
      if (match && match[1]) {
        return match[1]; // Es: "sub.webvto.it" -> "sub"
      }
      // Se include .webvto.it ma non matcha (es. ".webvto.it" o malformato),
      // restituisci la stringa processata per consentire la modifica.
      return processedUrl;
    }

    // Se non è un dominio .webvto.it, restituisci la stringa processata (senza protocollo)
    // Questo copre "customdomain", "another.domain.com", ecc.
    return processedUrl;

  } catch (error) {
    console.error('Error extracting subdomain:', error);
    return ''; // Fallback in caso di errore
  }
};

/**
 * Formats a domain to the full URL format
 * @param {string|object} domain - The domain to format
 * @returns {string} - The formatted URL
 */
export const formatFullDomainUrl = (domain) => {
  const subdomain = extractSubdomain(domain);
  if (!subdomain) return '';
  return `https://${subdomain}.webvto.it`;
};

/**
 * Ensures a domain value is always a string
 * @param {any} domain - The domain value to check
 * @returns {string} - A safe string representation of the domain
 */
export const getSafeDomain = (domain) => {
  if (!domain) return '';
  if (typeof domain === 'string') return domain;
  if (typeof domain === 'object' && domain !== null && typeof domain.url === 'string') return domain.url;
  return '';
};

/**
 * Ensures an image URL is always a valid string to prevent 'url.split is not a function' errors
 * @param {any} url - The URL value to check
 * @returns {string} - A safe string representation of the URL
 */
export const getSafeImageUrl = (url) => {
  // Handle null, undefined, or non-string/non-object values
  if (!url) return '';
  
  try {
    // If it's already a string, return it
    if (typeof url === 'string') return url;
    
    // If it's a File object, create an object URL
    if (url instanceof File) {
      return URL.createObjectURL(url);
    }
    
    // If it's an object from Ant Design Upload component
    if (typeof url === 'object') {
      // Check for originFileObj (Ant Design Upload format)
      if (url.originFileObj instanceof File) {
        return URL.createObjectURL(url.originFileObj);
      }
      
      // Check for direct url property
      if (typeof url.url === 'string') {
        return url.url;
      }
      
      // Check for thumbUrl (Ant Design Upload preview)
      if (typeof url.thumbUrl === 'string') {
        return url.thumbUrl;
      }
      
      // Check for name property (sometimes used as fallback)
      if (typeof url.name === 'string') {
        return url.name;
      }
    }
    
    // If we can't extract a valid URL, return empty string
    return '';
  } catch (error) {
    console.error('Error getting safe image URL:', error);
    return '';
  }
};
